import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PrivacidadPage() {
  const lastUpdated = '1 de abril de 2026';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <div className="bg-gray-900 text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3">Política de Privacidad</h1>
            <p className="text-gray-400 text-sm">Última actualización: {lastUpdated}</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="prose prose-gray max-w-none space-y-10 text-gray-700 text-sm leading-relaxed">

            {/* Intro */}
            <section>
              <p className="text-base text-gray-800">
                En <strong>IslaVende</strong> nos comprometemos a proteger su privacidad. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos su información personal cuando utiliza nuestra plataforma, de conformidad con la Ley de Privacidad de Puerto Rico y las leyes federales aplicables de los Estados Unidos.
              </p>
            </section>

            {/* 1 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">1. Información que Recopilamos</h2>
              <p className="mb-3 font-semibold text-gray-800">1.1 Información que usted nos proporciona:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Datos de registro:</strong> nombre completo, dirección de correo electrónico, contraseña, número de teléfono, dirección postal y pueblo de Puerto Rico.</li>
                <li><strong>Datos de verificación de vendedores:</strong> tipo y número de identificación oficial (licencia de conducir, pasaporte, ID estatal o REAL ID), imagen de la identificación.</li>
                <li><strong>Datos de transacciones:</strong> historial de compras y ventas, información de envío, estado de órdenes.</li>
                <li><strong>Comunicaciones:</strong> mensajes enviados a través de la plataforma o a nuestro equipo de soporte.</li>
              </ul>
              <p className="mb-3 font-semibold text-gray-800">1.2 Información recopilada automáticamente:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Dirección IP y datos del dispositivo.</li>
                <li>Páginas visitadas, tiempo de sesión y acciones dentro de la plataforma.</li>
                <li>Cookies y tecnologías similares para mejorar la experiencia del usuario.</li>
              </ul>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">2. Cómo Usamos su Información</h2>
              <p className="mb-3">Utilizamos su información personal para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Crear y gestionar su cuenta en IslaVende.</li>
                <li>Procesar transacciones de compra y venta de forma segura.</li>
                <li>Verificar la identidad de los vendedores para prevenir fraudes.</li>
                <li>Enviar notificaciones sobre el estado de sus órdenes, envíos y pagos.</li>
                <li>Enviar comunicaciones de servicio al cliente y soporte técnico.</li>
                <li>Cumplir con obligaciones legales y regulatorias aplicables en Puerto Rico.</li>
                <li>Mejorar la plataforma y personalizar su experiencia.</li>
                <li>Prevenir actividades fraudulentas, ilegales o que violen nuestros Términos y Condiciones.</li>
              </ul>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">3. Compartición de Información con Terceros</h2>
              <p className="mb-3">
                IslaVende <strong>no vende ni alquila</strong> su información personal a terceros. Podemos compartir información en los siguientes casos limitados:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Para procesar pagos de forma segura. Stripe tiene su propia política de privacidad certificada PCI-DSS.</li>
                <li><strong>USPS / Island Wide:</strong> Para coordinar el envío y entrega de artículos (nombre, dirección de envío).</li>
                <li><strong>Resend:</strong> Para el envío de correos electrónicos transaccionales (confirmaciones, notificaciones de estado).</li>
                <li><strong>Supabase:</strong> Como proveedor de infraestructura de base de datos y autenticación, con servidores en los Estados Unidos.</li>
                <li><strong>Autoridades legales:</strong> Cuando sea requerido por ley, orden judicial o para proteger los derechos de IslaVende o sus usuarios.</li>
              </ul>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">4. Almacenamiento y Seguridad de Datos</h2>
              <p className="mb-3">
                Su información se almacena en servidores seguros administrados por Supabase, ubicados en los Estados Unidos. Implementamos medidas de seguridad técnicas y organizativas que incluyen:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cifrado de datos en tránsito mediante TLS/SSL.</li>
                <li>Cifrado de contraseñas mediante algoritmos de hash seguros (bcrypt).</li>
                <li>Políticas de seguridad a nivel de fila (Row Level Security) en la base de datos.</li>
                <li>Acceso restringido a datos personales solo al personal autorizado.</li>
                <li>Imágenes de identificación almacenadas en buckets privados con acceso controlado.</li>
              </ul>
              <p className="mt-3">
                A pesar de nuestras medidas de seguridad, ningún sistema es completamente infalible. En caso de una brecha de seguridad que afecte sus datos, le notificaremos dentro de las 72 horas siguientes a su detección.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">5. Retención de Datos</h2>
              <p className="mb-3">Conservamos su información personal durante el tiempo que sea necesario para:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Mantener su cuenta activa y prestar los servicios solicitados.</li>
                <li>Cumplir con obligaciones legales, contables o fiscales (mínimo 7 años para registros de transacciones).</li>
                <li>Resolver disputas y hacer cumplir nuestros acuerdos.</li>
              </ul>
              <p className="mt-3">
                Al eliminar su cuenta, sus datos personales serán anonimizados o eliminados dentro de los 30 días siguientes, excepto aquellos que debamos conservar por obligación legal.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">6. Sus Derechos</h2>
              <p className="mb-3">Como usuario de IslaVende, usted tiene derecho a:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Acceso:</strong> Solicitar una copia de los datos personales que tenemos sobre usted.</li>
                <li><strong>Rectificación:</strong> Corregir información inexacta o incompleta.</li>
                <li><strong>Eliminación:</strong> Solicitar la eliminación de sus datos personales, sujeto a obligaciones legales.</li>
                <li><strong>Portabilidad:</strong> Recibir sus datos en un formato estructurado y legible por máquina.</li>
                <li><strong>Oposición:</strong> Oponerse al procesamiento de sus datos para fines de marketing.</li>
              </ul>
              <p className="mt-3">
                Para ejercer cualquiera de estos derechos, contáctenos en <a href="mailto:ventas@islavende.com" className="text-gray-900 font-semibold underline">ventas@islavende.com</a>. Responderemos dentro de los 30 días calendario.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">7. Cookies</h2>
              <p className="mb-3">
                IslaVende utiliza cookies esenciales para el funcionamiento de la plataforma (autenticación, sesión de usuario) y cookies analíticas para mejorar la experiencia. No utilizamos cookies de publicidad de terceros.
              </p>
              <p>
                Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad de la plataforma.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">8. Menores de Edad</h2>
              <p>
                IslaVende no está dirigida a menores de 18 años. No recopilamos intencionalmente información personal de menores. Si descubrimos que hemos recopilado datos de un menor sin el consentimiento parental, eliminaremos dicha información de inmediato.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">9. Cambios a esta Política</h2>
              <p>
                Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos mediante un aviso en la plataforma o por correo electrónico. El uso continuado de IslaVende después de la publicación de cambios constituye su aceptación de la política actualizada.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">10. Contacto</h2>
              <p>
                Para preguntas, solicitudes o inquietudes sobre esta Política de Privacidad:
              </p>
              <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-gray-900">IslaVende — Oficial de Privacidad</p>
                <p className="text-gray-600">Puerto Rico, Estados Unidos</p>
                <p className="text-gray-600">
                  Email: <a href="mailto:ventas@islavende.com" className="text-gray-900 font-semibold underline">ventas@islavende.com</a>
                </p>
              </div>
            </section>

            {/* Nav links */}
            <div className="border-t pt-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <Link to="/terminos" className="text-gray-900 font-semibold underline text-sm">
                Ver Términos y Condiciones →
              </Link>
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                ← Volver al inicio
              </Link>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
